import './TicketListFilter.scss';

import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useForm } from '@refinedev/antd';
import { Button, Checkbox, Form, Input, Popover } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import CompanySelector from '@/components/general/CompanySelector';
import FullscreenDrawer from '@/components/general/Drawers/FullscreenDrawer';
import { useUiContext } from '@/contexts/UiContext';
import { Ticket } from '@/model/Ticket/Ticket';
import {
  TICKET_LOGISTIC_STATE,
  TicketLogisticStateString,
  TicketStateString,
} from '@/model/Ticket/TicketConstants';

import useTicketListFilter from './useTicketListFilter';

interface TicketListFilterProps {
  filters: any[];
  setFilters: Function;
  setCurrent: Function;
}

const TicketListFilter = (props: TicketListFilterProps) => {
  const { isMobile } = useUiContext();
  const { filters, setFilters, setCurrent } = props;
  const { t } = useTranslation();
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const { formProps } = useForm<Ticket>({ warnWhenUnsavedChanges: false });
  const { form } = formProps;
  const {
    usersSelectProps,
    companySelectProps,
    subStatusSelectProps,
    subCategorySelectProps,
    countrySelectProps,
    machineSelectProps,
  } = useTicketListFilter(formProps, filters);
  const operationsAssociated = Form.useWatch('has_operations_associated', form);
  const applyFilters = useCallback(
    values => {
      const filters: any[] = [];

      // subestado
      if (values.subcategoria) {
        filters.push({
          field: 'subcategoria',
          operator: 'eq',
          value: values.subcategoria,
        });
      }

      if (values.search) {
        filters.push({
          field: 'search',
          operator: 'eq',
          value: values.search,
        });
      }

      // Empresa
      if (values.empresa) {
        filters.push({
          field: 'empresa',
          operator: 'eq',
          value: values.empresa,
        });
      }

      // Máquina
      if (values.movil) {
        filters.push({
          field: 'movil',
          operator: 'eq',
          value: values.movil,
        });
      }

      // Estado abierto / cerrado
      if (values.isOpen) {
        filters.push({
          field: 'is_open',
          operator: 'eq',
          value: values.isOpen,
        });
      }

      // Estado
      if (values.status) {
        filters.push({
          field: 'status',
          operator: 'eq',
          value: values.status,
        });
      }

      // subestado
      if (values.substatus) {
        filters.push({
          field: 'substatus',
          operator: 'eq',
          value: values.substatus,
        });
      }

      // assigned to
      if (values.assigned_to) {
        filters.push({
          field: 'assigned_to',
          operator: 'eq',
          value: values.assigned_to,
        });
      }

      // pais
      if (values.pais) {
        filters.push({
          field: 'pais',
          operator: 'eq',
          value: values.pais,
        });
      }

      // pais
      if (values.no_tracking) {
        filters.push({
          field: 'no_tracking',
          operator: 'eq',
          value: values.no_tracking == true ? 1 : undefined,
        });
      }

      // estado logístico
      if (values.has_operations_associated != undefined) {
        filters.push({
          field: 'has_operations_associated',
          operator: 'eq',
          value: values.has_operations_associated,
        });
      }

      // estado logístico
      if (values.logistics_state) {
        filters.push({
          field: 'logistics_state',
          operator: 'eq',
          value: values.logistics_state,
        });
      }

      setFilters(filters, 'replace');
      setCurrent(1);
      setFilterPopoverOpen(false);
    },
    [setFilters, setFilterPopoverOpen, setCurrent],
  );
  const handleClearFilters = useCallback(() => {
    form?.resetFields();
    setFilters([], 'replace');
    setCurrent(1);
    setFilterPopoverOpen(false);
  }, [form, setCurrent, setFilters]);
  const filterContent = (
    <Form
      labelAlign="left"
      {...formProps}
      style={{ marginBottom: 8 }}
      onFinish={applyFilters}
      className="TicketListFilterPopover">
      <div className="TicketListFilterPopoverRows">
        <Form.Item label={t('Buscar')} name={'search'} className="TicketListFilterRow">
          <Input />
        </Form.Item>
        <Form.Item label={t('Empresa')} name={'empresa'} className="TicketListFilterRow">
          <CompanySelector showFullName {...companySelectProps} allowClear showSearch />
          {/* <AntdSelect {...companySelectProps} allowClear showSearch /> */}
        </Form.Item>
        <Form.Item label={t('Máquina')} name={'movil'} className="TicketListFilterRow">
          <AntdSelect {...machineSelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Subcategoria')} name={'subcategoria'} className="TicketListFilterRow">
          <AntdSelect {...subCategorySelectProps} allowClear showSearch />
        </Form.Item>

        <Form.Item label={t('Estado')} name={'status'} className="TicketListFilterRow">
          <AntdSelect
            options={Object.keys(TicketStateString).map(v => ({
              label: TicketStateString[parseInt(v)],
              value: parseInt(v),
            }))}
            allowClear
          />
        </Form.Item>
        <Form.Item label={t('Subestado')} name={'substatus'} className="TicketListFilterRow">
          <AntdSelect {...subStatusSelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Asignado a')} name={'assigned_to'} className="TicketListFilterRow">
          <AntdSelect options={usersSelectProps.options} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Pais')} name={'pais'} className="TicketListFilterRow">
          <AntdSelect {...countrySelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item
          label={t('Sin seguimiento')}
          name={'no_tracking'}
          valuePropName="checked"
          className="TicketListFilterRow">
          <Checkbox />
        </Form.Item>
        <Form.Item
          label={t('Movimientos logísticos')}
          name={'has_operations_associated'}
          className="TicketListFilterRow">
          <AntdSelect
            options={[
              { value: 1, label: t('Con movimientos logísticos') },
              { value: 0, label: t('Sin movimientos logísticos') },
            ]}
            allowClear
          />
        </Form.Item>
        {operationsAssociated ? (
          <Form.Item
            label={t('Estado logístico')}
            name={'logistics_state'}
            className="TicketListFilterRow">
            <AntdSelect
              options={Object.keys(TicketLogisticStateString)
                .filter(
                  tls => parseInt(tls) !== Number(TICKET_LOGISTIC_STATE.SIN_GESTION_LOGISTICA),
                )
                .map(v => ({
                  label: TicketLogisticStateString[parseInt(v)],
                  value: parseInt(v),
                }))}
              allowClear
            />
          </Form.Item>
        ) : null}
      </div>

      <div className="TicketListFilterActions">
        <Button type="default" style={{ marginLeft: 8 }} onClick={handleClearFilters}>
          {t('Limpiar')}
        </Button>
        <Button htmlType="submit" type="primary">
          {t('Buscar')}
        </Button>
      </div>
    </Form>
  );

  return (
    <div className="TicketListFilter">
      {isMobile ? (
        <>
          <Button
            className={filters.length > 0 ? 'TicketListFilterApplied' : ''}
            onClick={() => setFilterPopoverOpen(true)}
            icon={<FilterOutlined />}
          />
          <FullscreenDrawer
            disablePush
            title={t('Filtros')}
            open={filterPopoverOpen}
            setOpen={setFilterPopoverOpen}>
            {filterContent}
          </FullscreenDrawer>
        </>
      ) : (
        <Popover
          openClassName="TicketListFilterPopover"
          content={filterContent}
          title={t('Filtros')}
          trigger="click"
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          placement="bottomRight">
          <Button
            className={filters.length > 0 ? 'TicketListFilterApplied' : ''}
            icon={filters.length > 0 ? <FilterFilled /> : <FilterOutlined />}>
            {t('Filtrar')}
          </Button>
        </Popover>
      )}
    </div>
  );
};

export default TicketListFilter;
